/**
 * Locale 工具函数
 * 用于在 india/brazil region 使用短代码后，统一处理 locale 比较和匹配
 */

// Locale 规范化映射：短代码 -> 完整代码
const localeNormalizeMap: Record<string, string> = {
  'hi': 'hi-IN',
  'en': 'en-US',
  'ur': 'ur-PK',
  'pt': 'pt-BR',
  'es': 'es-MX',
  // 完整代码映射到自己
  'hi-IN': 'hi-IN',
  'en-US': 'en-US',
  'ur-PK': 'ur-PK',
  'pt-BR': 'pt-BR',
  'es-MX': 'es-MX',
};

/**
 * 将短代码或完整代码规范化为完整的 locale 代码
 * @param locale - 短代码（如 'hi'）或完整代码（如 'hi-IN'）
 * @returns 完整的 locale 代码（如 'hi-IN'）
 *
 * @example
 * normalizeLocale('hi') // 'hi-IN'
 * normalizeLocale('hi-IN') // 'hi-IN'
 * normalizeLocale('en') // 'en-US'
 */
export function normalizeLocale(locale: string): string {
  return localeNormalizeMap[locale] || locale;
}

/**
 * 检查 locale 是否匹配指定的完整 locale 代码
 * 支持短代码和完整代码的比较
 *
 * @param locale - 当前 locale（可能是短代码或完整代码）
 * @param targetLocale - 目标完整 locale 代码
 * @returns 是否匹配
 *
 * @example
 * isLocale('hi', 'hi-IN') // true
 * isLocale('hi-IN', 'hi-IN') // true
 * isLocale('en', 'hi-IN') // false
 */
export function isLocale(locale: string, targetLocale: string): boolean {
  return normalizeLocale(locale) === targetLocale;
}

/**
 * 从 locale 映射对象中获取值，支持短代码自动匹配
 *
 * @param locale - 当前 locale（可能是短代码或完整代码）
 * @param localeMap - locale 到值的映射对象
 * @param defaultValue - 默认值（可选）
 * @returns 匹配的值或默认值
 *
 * @example
 * const imageMap = {
 *   'en-US': imgEn,
 *   'hi-IN': imgHi,
 *   'pt-BR': imgPt,
 * };
 * getLocaleValue('hi', imageMap, imgEn) // imgHi
 * getLocaleValue('hi-IN', imageMap, imgEn) // imgHi
 */
export function getLocaleValue<T>(
  locale: string,
  localeMap: Record<string, T>,
  defaultValue?: T
): T | undefined {
  const normalizedLocale = normalizeLocale(locale);
  return localeMap[normalizedLocale] ?? defaultValue;
}
